import { Editor, type Range } from '@tiptap/core';
import { CellCoords } from 'handsontable/base';
import { SelectionController } from 'handsontable/selection';
import { debounce } from 'lodash';

// Utils
import { hasCommandModifier, hasShiftKey } from '../../../../utils/keyboard/keyboardUtility';
import { isFormula } from '../../utils/dataUtils/formula/tableFormulaPredicateUtils';
import {
    getEndCol,
    getEndRow,
    getStartCol,
    getStartRow,
    getStartSelectionCoords,
} from '../../utils/selectionUtils/tableSelectionAccessors';
import { getCellRangeName } from '../../utils/selectionUtils/tableSelectionExcelNameUtils';

// Types
import { CellCoordsObj, CellSelection } from '../../../../../common/table/TableTypes';
import { TiptapContent } from '../../../../../common/tiptap/tiptapTypes';
import getTiptapNodeTextContent from '../../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import TiptapCellEditor from '../../modules/TiptapCellEditor';
import { getTiptapContentFromPlainText } from '../../modules/tiptapCellEditorContentUtils';
import { MilanoteHotPlugin } from '../MilanoteHotPlugin';
import { CellReference, findCellReferenceAtSelection, getCellReferences } from './cellReferences';
import { hasSpecialCharBeforeSelection } from './textHelpers';

export const FORMULA_CLICK_REFERENCE_PLUGIN_NAME = 'FormulaClickReference';

class FormulaClickReferenceHotPlugin extends MilanoteHotPlugin {
    // Tracks the range within the cell text that gets replaced when adding/updating a cell reference,
    // plus the optional anchor cell used when extending the reference with drag/shift-click.
    // When non-null, we're in "reference mode", which means clicking on a cell should
    // add/update the formula to reference it rather than selecting that cell.
    private currentReference: { range: Range; anchorCell?: CellCoordsObj } | null = null;

    private justReferencedCell = false;

    /**
     * This key will be used as a prop key when enabling this plugin in the HotTable component
     */
    static get PLUGIN_KEY(): string {
        return FORMULA_CLICK_REFERENCE_PLUGIN_NAME;
    }

    /**
     * The `enablePlugin` method is triggered on the `beforeInit` hook.
     * It should contain the plugin's initial setup and hook connections.
     * This method is run only if the `isEnabled` method returns `true`.
     */
    enablePlugin(): void {
        this.addHook('beforeOnCellMouseDown', (...args) => this.onBeforeOnCellMouseDown(...args));
        this.addHook('beforeOnCellMouseOver', (...args) => this.onBeforeOnCellMouseOver(...args));
        this.addHook('beforeChange', () => this.onBeforeChange());

        this.addMilanoteHook('onMilanoteCellEditorChange', (...args) =>
            this.debouncedOnMilanoteCellEditorChange(...args),
        );

        // The `super` method sets the `this.enabled` property to `true`.
        // It is a necessary step to update the plugin's settings properly.
        super.enablePlugin();
    }

    /******************
     * PLUGIN HOOKS
     ******************/

    onMilanoteCellEditorChange(content: TiptapContent): void {
        if (!this.milanoteHot.getActiveEditor()?.isOpened()) return;

        // If we referenced a cell using this plugin (using referenceCell(), which updates the Milanote Editor's value),
        // `onMilanoteCellEditorChange` will be called and we should ignore it.
        if (this.justReferencedCell) {
            this.justReferencedCell = false;
            return;
        }

        if (!this.milanoteHot.isListening()) return;

        const editor = this.getTiptapEditor();
        if (!editor) return;

        this.updateCellReferenceMode(content, editor);
    }

    debouncedOnMilanoteCellEditorChange = debounce(this.onMilanoteCellEditorChange, 50);

    onBeforeOnCellMouseDown(
        event: MouseEvent,
        coords: CellCoords,
        _TD: HTMLTableCellElement,
        _controller: SelectionController,
    ): void {
        // @ts-ignore - Handsontable private property
        if (event.isImmediatePropagationEnabled === false) return;

        this.syncCellReferenceModeToEditorSelection();

        if (!this.currentReference) return;

        // If in cell referencing mode, when clicking on another cell, we want to prevent the normal behaviour of
        // selecting the clicked cell, and instead reference the cell in the current textarea.

        event.stopImmediatePropagation();
        event.preventDefault();

        // If shift key is pressed, we want to select the cell range from the cell we started referencing from to
        // the cell we clicked on.
        if (hasShiftKey(event) && this.currentReference.anchorCell) {
            const { row: startRow, col: startCol } = this.currentReference.anchorCell;
            const { row: endRow, col: endCol } = coords.toObject();

            this.referenceCells([startRow, startCol, endRow, endCol]);
            return;
        }

        if (hasCommandModifier(event) && this.currentReference.anchorCell) {
            this.insertTextAtRangeEnd(',');
            if (!this.currentReference) return;

            const { to } = this.currentReference.range;
            this.setCellReferenceMode({ from: to, to });
        }

        this.currentReference = { range: this.currentReference.range, anchorCell: coords.toObject() };

        const { row, col } = coords.toObject();
        this.referenceCells([row, col, row, col]);
    }

    onBeforeOnCellMouseOver(
        event: MouseEvent,
        coords: CellCoords,
        _TD: HTMLTableCellElement,
        _controller: SelectionController,
    ): void {
        if (event.buttons !== 1 || !this.currentReference?.anchorCell) return;

        // If in cell referencing mode and currently referencing cells, when hovering over another cell, we want to
        // prevent the normal behaviour of selecting the cell range, and instead reference the cell range in the
        // current textarea.

        event.stopImmediatePropagation();
        event.preventDefault();

        const { row: startRow, col: startCol } = this.currentReference.anchorCell;
        const { row: endRow, col: endCol } = coords.toObject();
        this.referenceCells([startRow, startCol, endRow, endCol]);
    }

    onBeforeChange(): void {
        this.unsetCellReferenceMode();
    }

    /******************
     * HELPER FUNCTIONS
     ******************/

    updateCellReferenceMode(content: TiptapContent, editor: Editor): void {
        const currentText = getTiptapNodeTextContent(content);
        const selectionStart = this.getPlainTextSelectionStart(editor);

        // ***********************************************
        // 1. Ignore if input value is not a formula

        if (!isFormula(currentText)) {
            this.unsetCellReferenceMode();
            return;
        }

        const cellReferences = getCellReferences(currentText);

        // ***********************************************
        // 2. Determine whether or not to enter cell reference mode (where clicking on another cell will reference it)

        const containsSpecialCharBeforeSelection = hasSpecialCharBeforeSelection(currentText, selectionStart);
        const cellReferenceAtSelection = findCellReferenceAtSelection(cellReferences, selectionStart);

        if (cellReferenceAtSelection) {
            // Cursor is on an existing cell reference -- click should replace that cell reference
            this.setReplacementCellReferenceMode(cellReferenceAtSelection, currentText);
        } else if (containsSpecialCharBeforeSelection) {
            // Cursor follows an open paren, comma, operator etc -- click should add a new cell reference
            this.setCellReferenceMode({ from: selectionStart, to: selectionStart });
        } else {
            // Cursor is not at a cell reference or special character -- click should not reference a cell
            this.unsetCellReferenceMode();
        }
    }

    getTiptapEditor(): Editor | null {
        const editor: Editor = (this.milanoteHot.getMNEditor() as TiptapCellEditor)
            ?.getEditorInstance()
            ?.getEditor() as Editor;
        return editor ?? null;
    }

    syncCellReferenceModeToEditorSelection(): void {
        if (!this.milanoteHot.getActiveEditor()?.isOpened()) return;
        if (!this.milanoteHot.isListening()) return;

        const editor = this.getTiptapEditor();
        if (!editor) return;

        this.updateCellReferenceMode(editor.getJSON() as TiptapContent, editor);
    }

    getPlainTextSelectionStart(editor: Editor): number {
        return editor.state.doc.textBetween(0, editor.state.selection.from, '\n', '\n').length;
    }

    /**
     * Returns the editor text flattened to the plaintext view used by formula parsing.
     */
    getEditorPlainText(editor: Editor): string {
        return getTiptapNodeTextContent(editor.getJSON() as TiptapContent);
    }

    /**
     * Replaces the active formula reference range against plaintext and rewrites the editor as simple content.
     */
    replaceReferenceText(text: string): void {
        const editor = this.getTiptapEditor();
        if (!editor || !this.currentReference) return;

        const currentText = this.getEditorPlainText(editor);
        const { from, to } = this.currentReference.range;
        const nextText = `${currentText.slice(0, from)}${text}${currentText.slice(to)}`;
        const nextRange = { from, to: from + text.length };
        const nextReferences = getCellReferences(nextText);

        editor
            .chain()
            // This will reset any text-level styling (eg partial bolding), but that's fine because formulas are always plain text.
            .setContent(getTiptapContentFromPlainText(nextText))
            .applyFormulaCellHighlights(nextReferences)
            // Plain formula rewrites always use the simple single-paragraph content shape, so the caret
            // position is the plaintext offset plus the paragraph's opening document offset.
            .setTextSelection(nextRange.to + 1)
            .focus()
            .run();

        // Dispatch a onChange event here -- we're catching the text highlight updates above, but
        // still need to recalculate the cell highlights in the rest of the table.
        // (this means text highlights get calculated twice, but if we don't do it in the transaction
        // above the cell references all flash black for a frame)
        // @ts-ignore - Milanote custom hook
        this.milanoteHot.runHooks('onMilanoteCellEditorChange', editor.getJSON() as TiptapContent);

        this.currentReference = { ...this.currentReference, range: nextRange };
    }

    insertTextAtRangeEnd(text: string): void {
        if (!this.currentReference) return;

        this.replaceReferenceText(`${this.getCurrentReferenceText()}${text}`);
        if (!this.currentReference) return;

        this.setCellReferenceMode(this.currentReference.range);
    }

    getCurrentReferenceText(): string {
        const editor = this.getTiptapEditor();
        if (!editor || !this.currentReference) return '';

        const { from, to } = this.currentReference.range;
        return this.getEditorPlainText(editor).slice(from, to);
    }

    /******************
     * ENTERING & EXITING REFERENCE MODE
     ******************/

    setReplacementCellReferenceMode(cellReference: CellReference, currentText: string): void {
        const { cellSelection, cellSelectionName } = cellReference;
        const fromCellCoords = getStartSelectionCoords(cellSelection);
        const referenceRange = { from: cellReference.from, to: cellReference.to };

        if (currentText.slice(referenceRange.from, referenceRange.to) !== cellSelectionName) {
            this.unsetCellReferenceMode();
            return;
        }

        this.setCellReferenceMode(referenceRange, fromCellCoords);
    }

    setCellReferenceMode(range: Range, anchorCell?: CellCoordsObj): void {
        this.currentReference = { range, anchorCell };
    }

    unsetCellReferenceMode(): void {
        this.currentReference = null;
    }

    /******************
     * ACTUALLY REFERENCING CELLS
     ******************/

    referenceCells(cellSelection: CellSelection): void {
        if (!this.currentReference) return;

        if (
            getStartRow(cellSelection) < 0 ||
            getStartCol(cellSelection) < 0 ||
            getEndRow(cellSelection) < 0 ||
            getEndCol(cellSelection) < 0
        )
            return;

        // Update editor value to include the referenced cell range
        const cellSelectionName = getCellRangeName(cellSelection);
        this.justReferencedCell = true;
        this.replaceReferenceText(cellSelectionName);
    }
}

export default FormulaClickReferenceHotPlugin;
